import { useStyles } from "./FooterNavigation.style";
import { Typography } from "../../core/Typography/Typography";
import {
  TypographyColor,
  TypographyVariant,
} from "../../../constants/typography";
import { Icon } from "../../core/Icon/Icon";
import { useEffect, useState } from "react";
import {
  FOOTER_NAVIGATION,
  FOOTER_NAVIGATION_WIDTH,
} from "../../../constants/footerNavigation";
import { useLocation, useNavigate } from "react-router-dom";
import { PageRoutes } from "../../../constants/historyGaming";

interface IFooterNavigationProps {
  notificationPage?: string[];
  mainElement?: HTMLElement | null;
}

const FooterNavigation: React.FC<IFooterNavigationProps> = ({
  notificationPage,
  mainElement,
}) => {
  const classes = useStyles();
  const navigate = useNavigate();

  const location = useLocation();
  const [screenOffset, setScreenOffset] = useState(
    (window.innerWidth - FOOTER_NAVIGATION_WIDTH) / 2,
  );
  const [isScrollBottom, setScrollBottom] = useState(false);

  const onClickScrollButton = () => {
    if (mainElement) {
      mainElement?.scrollTo({
        top: isScrollBottom ? 0 : mainElement.scrollTop + window.innerHeight,
        behavior: "smooth",
      });
    } else {
      window.scrollTo({
        top: isScrollBottom ? 0 : window.scrollY + window.innerHeight / 3,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    if (location.pathname !== PageRoutes.HomePage) return;
    const checkScrollBottom = (event: Event) => {
      const eventTarget = event.target as HTMLElement;

      setScrollBottom(
        eventTarget.scrollHeight - eventTarget.scrollTop - window.innerHeight <=
          window.innerHeight / 2,
      );
    };
    mainElement?.addEventListener("scroll", checkScrollBottom);
    return () => {
      mainElement?.removeEventListener("scroll", checkScrollBottom);
    };
  }, [location.pathname, mainElement]);

  useEffect(() => {
    setScrollBottom(false);
    if (location.pathname === PageRoutes.HomePage) return;

    const checkScrollBottom = () => {
      const scrollHeight = document.documentElement.scrollHeight;
      setScrollBottom(
        scrollHeight > window.innerHeight * 1.5
          ? scrollHeight - window.scrollY - window.innerHeight <=
              window.innerHeight / 2
          : window.scrollY !== 0 &&
              scrollHeight === window.scrollY + window.innerHeight,
      );
    };
    window?.addEventListener("scroll", checkScrollBottom);
    return () => {
      window?.removeEventListener("scroll", checkScrollBottom);
    };
  }, [location.pathname]);

  useEffect(() => {
    const onResizeFooter = () => {
      setScreenOffset((window.innerWidth - FOOTER_NAVIGATION_WIDTH) / 2);
    };
    window?.addEventListener("resize", onResizeFooter);
    return () => {
      window?.removeEventListener("resize", onResizeFooter);
    };
  }, [screenOffset]);

  return (
    <div className={classes.root}>
      <div className={classes.footerBackdrop} />
      <svg
        className={classes.svg}
        xmlns="http://www.w3.org/2000/svg"
        width={window.innerWidth}
        height="72"
        viewBox={`0 0 ${window.innerWidth} 72`}
        fill="none"
      >
        <path
          id="footer-path"
          clipRule="evenodd"
          d={`M${screenOffset + 187} 35C${screenOffset + 199.355} 35 ${
            screenOffset + 210.215
          } 28.5981 ${screenOffset + 216.444} 18.9306C${
            screenOffset + 222.426
          } 9.64519 ${screenOffset + 230.954} 0 ${
            screenOffset + 242
          } 0H374C374.552 0 ${window.innerWidth} 0.447715 ${
            window.innerWidth
          } 1V71C375 71.5523 374.552 72 374 72H0.999995C0.44771 72 0 71.5523 0 71V1C0 0.447716 0.447715 0 1 0H${
            screenOffset + 132
          }C${screenOffset + 143.046} 0 ${screenOffset + 151.574} 9.64519 ${
            screenOffset + 157.556
          } 18.9306C${screenOffset + 163.785} 28.5981 ${
            screenOffset + 174.645
          } 35 ${screenOffset + 187} 35Z`}
          fill="white"
          fillOpacity="0.1"
        />
        <clipPath id="clip-footer-path">
          <use clipRule={"evenodd"} xlinkHref="#footer-path" x="0" y="0" />
        </clipPath>
      </svg>
      {FOOTER_NAVIGATION.map((item) => {
        if (!item?.path) {
          return (
            <div className={classes.item} key={item.id}>
              <div
                className={classes.iconContainer}
                onClick={onClickScrollButton}
                data-testid="down-icon"
              >
                <Icon
                  name={
                    isScrollBottom
                      ? "icon-bold-arrow-up-1"
                      : "icon-bold-arrow-down-3"
                  }
                  size={24}
                />
              </div>
            </div>
          );
        }
        return (
          <button
            key={item.id}
            className={classes.item}
            data-testid={`${item?.id}-tab`}
            onClick={() => navigate(item?.path)}
          >
            <div className={classes.notifyWrapper}>
              {notificationPage?.includes(item.id) && (
                <span className={classes.notifyBadge} />
              )}
              <Icon
                name={
                  window.location.pathname === item.path
                    ? item.iconActive
                    : item.icon
                }
                className={
                  window.location.pathname === item?.path
                    ? classes.iconActive
                    : classes.icon
                }
              />
            </div>
            <Typography
              variant={TypographyVariant.BodyB6Medium}
              color={TypographyColor.Primary}
              lines={1}
            >
              {item.title}
            </Typography>
          </button>
        );
      })}
    </div>
  );
};

export default FooterNavigation;
